import React, { useEffect, useState } from "react";
import { Box, Typography, Avatar } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Sidebar from "./sidebar";
import ProjectFilter from "./ProjectFilter";
import CustomToolbar from "./CustomToolbarPrint";
import BeneficiarySearch from "./BeneficiarySearch";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { matchesProject } from "../utils/reportUtils";

const columns = [
  { field: "concDate", headerName: "Date", width: 140 },
  { field: "concType", headerName: "Type", width: 140 },
  { field: "concLog", headerName: "Logged By", width: 180 },
  {
    field: "concComment",
    headerName: "Comment",
    width: 380,
    renderCell: (params) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        {params.value}
      </div>
    ),
  },
  {
    field: "concImage",
    headerName: "Picture",
    width: 260,
    sortable: false,
    renderCell: (params) =>
      params.value ? (
        <a href={params.value} target="_blank" rel="noreferrer">
          <img
            src={params.value}
            alt="Case study"
            style={{
              width: 220,
              height: 160,
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        </a>
      ) : (
        ""
      ),
  },
];

const caseStudyRows = (person) => {
  if (!person) return [];
  return (person.conc || [])
    .filter((c) => c && (c.concType || "").trim().toLowerCase() === "case study")
    .slice()
    .sort((a, b) =>
      String(b.concDate || "").localeCompare(String(a.concDate || ""))
    )
    .map((c, index) => ({
      id: c.id ?? `${person.id}-${index}`,
      concDate: c.concDate || "",
      concType: c.concType || "",
      concLog: c.concLog || "",
      concComment: c.concComment || "",
      concImage: c.concImage || "",
    }));
};

export default function CaseStudyReport() {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [projectFilter, setprojectFilter] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const load = async () => {
      const querySnapshot = await getDocs(collection(db, "employee"));
      const list = [];
      querySnapshot.forEach((document) => {
        const e = document.data();
        list.push({
          id: document.id,
          firstName: e.firstName || "",
          surName: e.surName || "",
          gender: e.gender || "",
          profilePic: e.profilePic || "",
          postCode: e.postCode || "",
          DOB: e.DOB || "",
          conc: Array.isArray(e.conc) ? e.conc : [],
        });
      });
      list.sort((a, b) =>
        `${a.firstName} ${a.surName}`
          .trim()
          .localeCompare(`${b.firstName} ${b.surName}`.trim(), undefined, {
            sensitivity: "base",
          })
      );
      setBeneficiaries(list);
    };
    load();
  }, []);

  const updateRows = (person, projects) => {
    if (!person || !matchesProject(person.gender, projects)) {
      setRows([]);
      return;
    }
    setRows(caseStudyRows(person));
  };

  const handleSelect = (person) => {
    setSelected(person);
    updateRows(person, projectFilter);
  };

  const handleProjects = (projects) => {
    setprojectFilter(projects);
    if (selected && !matchesProject(selected.gender, projects)) {
      setSelected(null);
      setRows([]);
      return;
    }
    updateRows(selected, projects);
  };

  const availableBeneficiaries = beneficiaries.filter((beneficiary) =>
    matchesProject(beneficiary.gender, projectFilter)
  );
  const hasProject = projectFilter.length > 0;

  const optionLabel = (option) =>
    `${option.firstName} ${option.surName}`.trim();

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        style={{ marginTop: "50px" }}
      >
        <div style={{ width: "100%" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Case Study
          </Typography>

          <ProjectFilter
            value={projectFilter}
            onChange={handleProjects}
            style={{ width: "100%" }}
          />

          <BeneficiarySearch
            options={availableBeneficiaries}
            value={selected}
            onChange={(person) => handleSelect(person)}
            getOptionLabel={optionLabel}
            getOptionSecondary={(option) =>
              option.DOB
                ? `DOB ${option.DOB}`
                : option.postCode
                ? `Postcode ${option.postCode}`
                : ""
            }
            label="Search for a beneficiary"
            placeholder={
              hasProject
                ? "Type a first name or surname"
                : "Select a project first"
            }
            disabled={!hasProject}
            noOptionsText={
              hasProject
                ? "No beneficiaries found"
                : "Select a project first"
            }
            sx={{ width: "100%" }}
          />

          {selected ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: 3,
                mb: 2,
              }}
            >
              {selected.profilePic ? (
                <Avatar
                  src={selected.profilePic}
                  sx={{ width: 56, height: 56 }}
                />
              ) : (
                <Avatar sx={{ width: 56, height: 56 }}>
                  {selected.firstName ? selected.firstName[0] : "?"}
                </Avatar>
              )}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {optionLabel(selected)}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ mt: 3, mb: 2, color: "text.secondary" }}>
              {hasProject
                ? "Select a beneficiary to view their case studies."
                : "Select a project to search beneficiaries."}
            </Typography>
          )}

          <div style={{ height: 480, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[5, 10, 20]}
              getRowHeight={() => "auto"}
              components={{ Toolbar: CustomToolbar }}
            />
          </div>
        </div>
      </Box>
    </Box>
  );
}
